import React, { KeyboardEventHandler, MouseEventHandler, useEffect, useRef } from 'react';

import { ButtonBase, Tooltip } from '@material-ui/core';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import clsx from 'clsx';
import PubSub from 'pubsub-js';

import EVENTS from 'config/events/pubsub';
import useBoolState from 'hooks/useBoolState';
import useSubscription from 'hooks/useSubscription';

import useStyles from './ContextMenu.styles';

export type ContextMenuElement = {
  label: string;
  onClick: (data?: unknown) => void;
  disabled?: boolean;
  disabledTooltip?: string;
  icon?: React.ReactNode;
};

type Props = {
  children: React.ReactElement<any, any>;
  elements: ContextMenuElement[];
  ariaLabel?: string;
  id?: string;
};

const MENU_BUTTON_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"])';

const isNativeInteractiveElement = (element: React.ReactElement<any, any>): boolean => {
  if (typeof element.type !== 'string') return true;
  return ['button', 'a', 'input', 'select', 'textarea'].includes(element.type);
};

const TooltipContent: React.FC<{
  elements: ContextMenuElement[];
  closeTooltip: () => void;
  focusTrigger: () => void;
  menuId: string;
}> = ({ elements, closeTooltip, focusTrigger, menuId }) => {
  useEffect(() => {
    requestAnimationFrame(() => {
      const firstItem = document.getElementById(menuId)?.querySelector<HTMLElement>(MENU_BUTTON_SELECTOR);
      firstItem?.focus();
    });
  }, [menuId]);

  const preventPropagation = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, callback: (data?: any) => void, isDisabled?: boolean) => {
    e.stopPropagation();
    if (isDisabled) return;
    callback();
    closeTooltip();
    focusTrigger();
  };

  const handleMenuKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    const menuItems = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(MENU_BUTTON_SELECTOR));
    const currentIndex = menuItems.findIndex(element => element === document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      closeTooltip();
      focusTrigger();
      return;
    }

    if (!menuItems.length) return;

    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
      event.preventDefault();
      menuItems[(currentIndex + 1) % menuItems.length].focus();
    }

    if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
      event.preventDefault();
      menuItems[(currentIndex - 1 + menuItems.length) % menuItems.length].focus();
    }

    if (event.key === 'Home') {
      event.preventDefault();
      menuItems[0].focus();
    }

    if (event.key === 'End') {
      event.preventDefault();
      menuItems[menuItems.length - 1].focus();
    }
  };

  const styles = useStyles();

  const renderMenuItem = ({ label, onClick, disabled, disabledTooltip, icon }: ContextMenuElement) => {
    const isDisabledWithTooltip = Boolean(disabled && disabledTooltip);
    const menuItem = (
      <ButtonBase
        aria-disabled={disabled ? 'true' : undefined}
        className={clsx(styles.button, disabled && styles.buttonDisabled)}
        data-testid={`${label}-tooltip-button`}
        disabled={Boolean(disabled && !disabledTooltip)}
        disableRipple={disabled}
        disableTouchRipple={disabled}
        onClick={e => preventPropagation(e, onClick, disabled)}
        role='menuitem'
        tabIndex={disabled ? -1 : undefined}
      >
        {icon && <span className={styles.icon}>{icon}</span>}
        {label}
      </ButtonBase>
    );

    if (isDisabledWithTooltip) {
      return (
        <Tooltip enterDelay={300} placement='right' title={disabledTooltip ?? ''}>
          <span className={styles.disabledMenuItemWrapper}>{menuItem}</span>
        </Tooltip>
      );
    }

    return menuItem;
  };

  return (
    <div className={styles.tooltipContent} id={menuId} onKeyDown={handleMenuKeyDown} role='menu' tabIndex={-1}>
      {elements.map(element => (
        <React.Fragment key={element.label}>{renderMenuItem(element)}</React.Fragment>
      ))}
    </div>
  );
};

const ContextMenu: React.FC<Props> = ({ children, elements, ariaLabel = 'Open actions menu', id }) => {
  const { state, setFalse, toggle } = useBoolState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const menuId = `${id || 'context-menu'}-menu`;

  const focusTrigger = () => triggerRef.current?.focus();

  const openMenu = () => {
    if (!state) toggle();
  };

  const onOpen: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation();
    toggle();
  };

  const onTriggerKeyDown: KeyboardEventHandler<HTMLElement> = event => {
    children.props.onKeyDown?.(event);
    if (event.defaultPrevented) return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      openMenu();
    }

    if (event.key === 'Escape' && state) {
      event.preventDefault();
      setFalse();
    }
  };

  useSubscription(EVENTS.CONTEXT_MENU_OPENED, (_: string, eventData: { id: string }) => {
    if (eventData.id !== id) setFalse();
  });

  useEffect(() => {
    if (state && id) PubSub.publish(EVENTS.CONTEXT_MENU_OPENED, { id });
  }, [state]);

  const styles = useStyles();
  const childProps = children.props;
  const triggerProps = {
    ...childProps,
    'aria-controls': state ? menuId : undefined,
    'aria-expanded': state,
    'aria-haspopup': 'menu',
    'aria-label': childProps['aria-label'] || ariaLabel,
    onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      childProps.onClick?.(event);
      if (!event.defaultPrevented) onOpen(event);
    },
    onKeyDown: onTriggerKeyDown,
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      const { ref } = children as any;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    role: childProps.role || (isNativeInteractiveElement(children) ? undefined : 'button'),
    tabIndex: childProps.tabIndex ?? (isNativeInteractiveElement(children) ? undefined : 0),
  };
  const trigger = React.cloneElement(children, triggerProps);

  return (
    <ClickAwayListener onClickAway={setFalse}>
      <Tooltip
        classes={{ tooltip: styles.tooltip }}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        interactive
        onClose={setFalse}
        open={state}
        title={<TooltipContent closeTooltip={setFalse} elements={elements} focusTrigger={focusTrigger} menuId={menuId} />}
      >
        {trigger}
      </Tooltip>
    </ClickAwayListener>
  );
};

export default ContextMenu;
